import {
  $isListNode,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $getSelection, $isRangeSelection } from 'lexical';
import React, { useEffect, useState } from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { Image } from '../../element/Image/Image';
import { Button } from '../../element/Button/Button';
import BulletPointIcon from '../../../assets/rte-icons/icon-bullet-point.svg?url';

export type RawTextareaToolbarProps = {
  name: string;
};

/**
 * `dispatchCommand` requires a payload argument, but the list commands take a
 * void payload. A named constant avoids a bare `undefined` literal, which
 * `unicorn/no-useless-undefined` strips.
 */
const NO_PAYLOAD = undefined;

/**
 * Whether the selection sits inside a bulleted list, which drives both the
 * button's pressed state and which command a click dispatches.
 *
 * Reads the nearest list ancestor rather than the top-level block, so a bulleted
 * sublist reports bullet whatever encloses it. Must be called inside `read` or
 * `update`.
 */
const $isBulletListSelection = () => {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) return false;

  const listNode = $getNearestNodeOfType(selection.anchor.getNode(), ListNode);

  return $isListNode(listNode) && listNode.getListType() === 'bullet';
};

/**
 * Bulleted list is the only button, because it is the only editor action with an
 * icon in `src/assets/rte-icons`. Everything below is already supported by the
 * editor and round-trips through markdown — it simply has no toolbar affordance
 * yet, pending icons from the design system.
 *
 * Users can still reach all of it: markdown shortcuts fire as you type (`- `,
 * `1. `, `# `, `> `, `**bold**`, `` `code` ``), pasted content converts, Tab
 * nests list items, and anything already in a stored value renders.
 *
 * NOT IMPLEMENTED — each is one `<Button>` plus the call shown:
 *
 * | Action        | Call                                                          |
 * | ------------- | ------------------------------------------------------------- |
 * | Bold          | `dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')`                |
 * | Italic        | `dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')`              |
 * | Strikethrough | `dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')`       |
 * | Inline code   | `dispatchCommand(FORMAT_TEXT_COMMAND, 'code')`                |
 * | Highlight     | `dispatchCommand(FORMAT_TEXT_COMMAND, 'highlight')` (`==x==`) |
 * | Numbered list | `dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)`      |
 * | Checklist     | `dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)`        |
 * | Undo / redo   | `dispatchCommand(UNDO_COMMAND / REDO_COMMAND, undefined)`      |
 *
 * Each of those also wants what the bullet button already does: state read off
 * the selection, so the button can render pressed and toggle rather than only
 * apply. Lexical ships no toggle command — insert and remove are separate — so
 * the other two list types reuse `$isBulletListSelection` with their own list
 * type, and the text formats read `selection.hasFormat(format)` in the same
 * update listener.
 *
 * Block types need a selection, via `$setBlocksType` from `@lexical/selection`
 * inside `editor.update()`:
 *
 * | Heading h1–h6 | `$createHeadingNode('h1')` — `@lexical/rich-text`              |
 * | Quote         | `$createQuoteNode()` — `@lexical/rich-text`                     |
 * | Code block    | `$createCodeNode()` — `@lexical/code-core`                      |
 *
 * Two need UI beyond a button:
 *
 * - **Link** — `dispatchCommand(TOGGLE_LINK_COMMAND, url | null)` from
 *   `@lexical/link`. Needs somewhere to enter the URL (dialog or floating
 *   toolbar). Note stored values already contain links, so this is authoring
 *   only; rendering works today.
 * - **Table** — `dispatchCommand(INSERT_TABLE_COMMAND, { columns, rows })` from
 *   `@lexical/table`, both strings. Needs a size picker, and realistically
 *   add/remove row and column controls once a table exists. Column alignment is
 *   parsed but re-exported as `---`; see `textarea-table-transformer.ts`.
 *
 * Deliberately excluded, not pending icons:
 *
 * - **Underline** and **text alignment** have no markdown representation, so
 *   neither survives a save. `icon-underline.svg` and the three alignment icons
 *   in `src/assets/rte-icons` are leftovers from the previous editor.
 *
 * Design input is worth having on grouping as well as icons — lists and tables
 * are usually dropdowns rather than a flat row of buttons.
 */
export const RawTextareaToolbar = ({ name }: RawTextareaToolbarProps) => {
  const [editor] = useLexicalComposerContext();
  const [isBulletList, setIsBulletList] = useState(false);

  /**
   * Lexical treats a selection change as an update, so this covers clicking
   * into a list as well as editing one. The initial read matters because the
   * toolbar can mount with a selection already in place — `full` flipping on, or
   * `disabled` flipping off, remounts it mid-edit.
   */
  useEffect(() => {
    const readIsBulletList = () => {
      setIsBulletList(editor.getEditorState().read($isBulletListSelection));
    };

    readIsBulletList();

    return editor.registerUpdateListener(readIsBulletList);
  }, [editor]);

  return (
    <div className="raw-textarea-toolbar" data-test={`${name}-toolbar`}>
      <Button
        className={AttrsHelper.formatClassname(
          'raw-textarea-toolbar-button',
          isBulletList && 'active-state'
        )}
        name="BULLET_LIST"
        onClick={() => {
          /**
           * `INSERT_UNORDERED_LIST_COMMAND` only ever converts *to* a bullet
           * list — dispatching it inside one rebuilds an identical list, so the
           * click would appear to do nothing. Removing is a separate command.
           */
          editor.dispatchCommand(
            isBulletList ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
            NO_PAYLOAD
          );
        }}
        pattern="secondary"
        title="Bulleted list"
        variation="minimal"
      >
        <Image alt="Bulleted list" src={BulletPointIcon} />
      </Button>
    </div>
  );
};
